import { useToast } from '@chakra-ui/react'
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query'
import {
  fetchContent,
  addContent,
  fetchContentList,
  updateContent,
  patchContent,
  deleteContent,
  publishContent,
  
} from '../../api/content/content'
import api from '../../api/api' // <-- Add this import for 'api'
import handleToast from '../../components/HandleToast'
import { ContentDataPost } from '../../types/content/content'
import { useState } from 'react'

export function content() {
  const toast = useToast()
  const queryClient = useQueryClient()

  const [isLoading, setIsLoading] = useState(false)

  const getContentListQuery = ({ page, pageSize }: { page: number; pageSize: number }) =>
    useQuery({
      queryKey: ['content'],
      queryFn: () => fetchContentList({ page, pageSize, search: '', filters: {} }),
    })

  const createContentMutation = useMutation({
    mutationFn: async (content: ContentDataPost) => {
      setIsLoading(true)
      await addContent(content)
      setIsLoading(false)
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ['content'] })
    },
    onError: (error: Error) => {
      handleToast(toast, false, 'Create content failed', error.message)
    },
  })

  const getContentQuery = useMutation({
    mutationFn: (contentId: string) => fetchContent(contentId),
    onSuccess: async () => {},
    onError: (error: Error) => {
      handleToast(toast, false, 'Get content failed', error.message)
    },
  })

  const updateContentMutation = useMutation({
    mutationFn: (variables: { contentId: string; contentData: ContentDataPost }) =>
      updateContent(variables.contentId, variables.contentData),
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ['content'] })
      handleToast(toast, true, 'Update content success', 'Content has been updated')
    },
    onError: (error: Error) => {
      handleToast(toast, false, 'Update content failed', error.message)
    },
  })

  const patchContentMutation = useMutation({
    mutationFn: async ({ contentId, contentData }: { contentId: string, contentData: any }) => {
      
      if (contentData.page_durations) {
        const fixedDurations: Record<string, number> = {};
        Object.entries(contentData.page_durations).forEach(([key, value]) => {
          const duration = Number(value);
          if (duration > 0) {
            fixedDurations[String(key)] = duration;
          }
        });
        contentData.page_durations = fixedDurations;
      }
      
      const response = await api.patch(`/content/${contentId}/`, contentData);
      return response.data;
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ['content'] })
      //handleToast(toast, true, 'Patch content success', 'Content has been patched')
    },
    onError: (error: Error) => {
      handleToast(toast, false, 'Update content failed', error.message)
    },
  })

  const deleteContentMutation = useMutation({
    mutationFn: (variables: { contentId: string }) => deleteContent(variables.contentId),
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ['comments'] })
      handleToast(toast, true, 'Delete content success', 'Content has been deleted')
    },
    onError: (error: Error) => {
      handleToast(toast, false, 'Delete content failed', error.message)
    },
  })

  const patchPublishContentMutation = useMutation({
    mutationFn: (contentId: string) => publishContent(contentId),
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ['content'] })
      handleToast(toast, true, 'Content published!', 'Content has been published successfully')
    },
    onError: (error: Error) => {
      handleToast(toast, false, 'Update content failed', error.message)
    },
  })

  return {
    createContentMutation,
    getContentListQuery,
    getContentQuery,
    updateContentMutation,
    patchContentMutation,
    deleteContentMutation,
    patchPublishContentMutation,
    isLoading,
  }
}
